import { getShared, setShared, setSharedNoWait } from './sharedSettings';
export interface AppTheme {
  id: string;
  label: string;
  accent: string;
  accentHover: string;
  accentLight: string;
  accentText: string;
  sidebar: string;
  sidebarHover: string;
  sidebarBorder: string;
  sidebarText: string;
  sidebarActive: string;
}

import { HEX, shade, tint, isDark, luminance } from './colorMath';
import { buildDarkCSS } from './darkCSS';

/** Barvy postranního panelu odvozené z jeho základní barvy. */
export function deriveSidebar(base: string, accent: string): Pick<AppTheme, 'sidebar' | 'sidebarHover' | 'sidebarBorder' | 'sidebarText' | 'sidebarActive'> {
  if (isDark(base)) {
    return { sidebar: base, sidebarHover: tint(base, 0.09), sidebarBorder: tint(base, 0.15), sidebarText: tint(base, 0.74), sidebarActive: accent };
  }
  return { sidebar: base, sidebarHover: shade(base, 0.05), sidebarBorder: shade(base, 0.1), sidebarText: '#374151', sidebarActive: accent };
}
/** Barvy akcentu odvozené z jedné barvy. */
export function deriveAccent(accent: string): Pick<AppTheme, 'accent' | 'accentHover' | 'accentLight' | 'accentText'> {
  return { accent, accentHover: shade(accent, 0.13), accentLight: tint(accent, 0.88), accentText: shade(accent, 0.22) };
}
function dark(id: string, label: string, accent: string, sidebar: string): AppTheme {
  return { id, label, ...deriveAccent(accent), ...deriveSidebar(sidebar, accent) };
}
function light(id: string, label: string, accent: string): AppTheme {
  return { id, label, ...deriveAccent(accent), ...deriveSidebar('#ffffff', accent) };
}

export const THEMES: AppTheme[] = [
  {
    id: 'default',
    label: 'Výchozí (Modrá)',
    accent:       '#2563eb',
    accentHover:  '#1d4ed8',
    accentLight:  '#dbeafe',
    accentText:   '#1d4ed8',
    sidebar:      '#111827',
    sidebarHover: '#1f2937',
    sidebarBorder:'#374151',
    sidebarText:  '#d1d5db',
    sidebarActive:'#2563eb',
  },
  {
    id: 'ocean',
    label: 'Oceán',
    accent:       '#0891b2',
    accentHover:  '#0e7490',
    accentLight:  '#cffafe',
    accentText:   '#0e7490',
    sidebar:      '#0c4a6e',
    sidebarHover: '#075985',
    sidebarBorder:'#0369a1',
    sidebarText:  '#bae6fd',
    sidebarActive:'#0891b2',
  },
  {
    id: 'forest',
    label: 'Les',
    accent:       '#16a34a',
    accentHover:  '#15803d',
    accentLight:  '#dcfce7',
    accentText:   '#15803d',
    sidebar:      '#14532d',
    sidebarHover: '#166534',
    sidebarBorder:'#15803d',
    sidebarText:  '#bbf7d0',
    sidebarActive:'#16a34a',
  },
  {
    id: 'purple',
    label: 'Fialová',
    accent:       '#7c3aed',
    accentHover:  '#6d28d9',
    accentLight:  '#ede9fe',
    accentText:   '#6d28d9',
    sidebar:      '#1e1b4b',
    sidebarHover: '#312e81',
    sidebarBorder:'#3730a3',
    sidebarText:  '#c4b5fd',
    sidebarActive:'#7c3aed',
  },
  {
    id: 'sunset',
    label: 'Západ slunce',
    accent:       '#ea580c',
    accentHover:  '#c2410c',
    accentLight:  '#ffedd5',
    accentText:   '#c2410c',
    sidebar:      '#1c1917',
    sidebarHover: '#292524',
    sidebarBorder:'#44403c',
    sidebarText:  '#d6d3d1',
    sidebarActive:'#ea580c',
  },
  {
    id: 'rose',
    label: 'Červená',
    accent:       '#e11d48',
    accentHover:  '#be123c',
    accentLight:  '#ffe4e6',
    accentText:   '#be123c',
    sidebar:      '#1f1f1f',
    sidebarHover: '#2d2d2d',
    sidebarBorder:'#404040',
    sidebarText:  '#e5e5e5',
    sidebarActive:'#e11d48',
  },
  {
    id: 'slate',
    label: 'Šedá / Tmavá',
    accent:       '#475569',
    accentHover:  '#334155',
    accentLight:  '#f1f5f9',
    accentText:   '#334155',
    sidebar:      '#0f172a',
    sidebarHover: '#1e293b',
    sidebarBorder:'#334155',
    sidebarText:  '#94a3b8',
    sidebarActive:'#475569',
  },
  light('cloud',  'Světlý – modrý',   '#2563eb'),
  light('sky',    'Světlý – nebe',    '#0ea5e9'),
  light('lime',   'Světlý – zelený',  '#16a34a'),
  dark('indigo',  'Indigo',           '#4f46e5', '#0f172a'),
  dark('mint',    'Mátová',           '#0d9488', '#134e4a'),
  dark('amber',   'Jantar',           '#d97706', '#18181b'),
  dark('midnight','Půlnoc',           '#6366f1', '#0b1020'),
  dark('berry',   'Bobule',           '#c026d3', '#2e1065'),
];

// ── Nastavení vzhledu nad rámec barevného tématu ─────────────────────────────

export type SidebarMode = 'preset' | 'dark' | 'light' | 'accent' | 'custom';
export type BgMode = 'default' | 'white' | 'cool' | 'warm' | 'mint' | 'lavender' | 'tint' | 'gradient';
export type TopbarMode = 'light' | 'tint' | 'clear';
export type RadiusMode = 'sharp' | 'normal' | 'soft' | 'round';
export type ButtonShape = 'follow' | 'square' | 'pill';
export type ButtonStyle = 'solid' | 'gradient' | 'soft';
export type CardMode = 'default' | 'shadow' | 'flat';
export type FontScale = 'sm' | 'md' | 'lg' | 'xl';
export type FontFamily = 'default' | 'rounded' | 'serif' | 'narrow';
export type Density = 'compact' | 'normal' | 'roomy';

export interface UiConfig {
  accent?: string;          // vlastní barva akcentu (#rrggbb)
  sidebar?: SidebarMode;
  sidebarColor?: string;    // pro sidebar = 'custom'
  bg?: BgMode;
  topbar?: TopbarMode;
  radius?: RadiusMode;
  buttonShape?: ButtonShape;
  buttonStyle?: ButtonStyle;
  cards?: CardMode;
  fontScale?: FontScale;
  font?: FontFamily;
  density?: Density;
}

const pick = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;

/** Zvaliduje uložené nastavení – cokoli neznámého se zahodí (barvy jdou do CSS, proto přísná kontrola). */
export function sanitizeUi(raw: unknown): UiConfig {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const o: UiConfig = {};
  if (typeof r.accent === 'string' && HEX.test(r.accent)) o.accent = r.accent.toLowerCase();
  if (typeof r.sidebarColor === 'string' && HEX.test(r.sidebarColor)) o.sidebarColor = r.sidebarColor.toLowerCase();
  const set = <K extends keyof UiConfig>(k: K, v: UiConfig[K] | undefined) => { if (v !== undefined) o[k] = v; };
  set('sidebar', pick(r.sidebar, ['preset', 'dark', 'light', 'accent', 'custom'] as const));
  set('bg', pick(r.bg, ['default', 'white', 'cool', 'warm', 'mint', 'lavender', 'tint', 'gradient'] as const));
  set('topbar', pick(r.topbar, ['light', 'tint', 'clear'] as const));
  set('radius', pick(r.radius, ['sharp', 'normal', 'soft', 'round'] as const));
  set('buttonShape', pick(r.buttonShape, ['follow', 'square', 'pill'] as const));
  set('buttonStyle', pick(r.buttonStyle, ['solid', 'gradient', 'soft'] as const));
  set('cards', pick(r.cards, ['default', 'shadow', 'flat'] as const));
  set('fontScale', pick(r.fontScale, ['sm', 'md', 'lg', 'xl'] as const));
  set('font', pick(r.font, ['default', 'rounded', 'serif', 'narrow'] as const));
  set('density', pick(r.density, ['compact', 'normal', 'roomy'] as const));
  if (o.sidebar === 'custom' && !o.sidebarColor) delete o.sidebar;
  return o;
}

const UI_KEY = 'ui_cfg';

export function getUiConfig(): UiConfig {
  try { return sanitizeUi(JSON.parse(getShared(UI_KEY) ?? 'null')); } catch { return {}; }
}

/** Téma po zohlednění vlastní barvy akcentu a režimu postranního panelu. */
export function resolveTheme(themeId: string, cfg: UiConfig, dark = false): AppTheme {
  let t: AppTheme = { ...(THEMES.find(x => x.id === themeId) ?? THEMES[0]) };
  if (cfg.accent) t = { ...t, ...deriveAccent(cfg.accent), sidebarActive: cfg.accent };
  const base = cfg.sidebar === 'dark' ? '#111827'
    : cfg.sidebar === 'light' ? '#ffffff'
    : cfg.sidebar === 'accent' ? shade(t.accent, 0.5)
    : cfg.sidebar === 'custom' ? cfg.sidebarColor
    : undefined;
  if (base) t = { ...t, ...deriveSidebar(base, t.accent) };
  // v tmavém režimu je světlý panel nevhodný – použije se tmavý
  if (dark && !isDark(t.sidebar)) t = { ...t, ...deriveSidebar('#10151c', t.accent) };
  return t;
}

export const BG_COLORS: Record<Exclude<BgMode, 'tint' | 'gradient'>, string> = {
  default: '#f9fafb', white: '#ffffff', cool: '#f1f5f9', warm: '#faf7f2', mint: '#f0f7f4', lavender: '#f5f3ff',
};
export const FONT_STACKS: Record<FontFamily, string> = {
  default: '',
  rounded: "ui-rounded,'Nunito','Trebuchet MS','Segoe UI',Arial,sans-serif",
  serif: "Georgia,'Times New Roman',serif",
  narrow: "'Arial Narrow','Roboto Condensed','Segoe UI',Arial,sans-serif",
};
const SCALE_PX: Record<FontScale, number> = { sm: 14, md: 16, lg: 17.5, xl: 19 };
const RADII: Record<Exclude<RadiusMode, 'normal'>, [number, number, number, number]> = {
  sharp: [2, 3, 5, 7], soft: [8, 12, 16, 20], round: [10, 14, 20, 26],
};

export function buildThemeCSS(t: AppTheme, cfg: UiConfig = {}): string {
  const lightSidebar = !isDark(t.sidebar);
  const SOLID_BTN = ':is(button,a)[class*="text-white"][class*="bg-"]:not([class*="rounded-full"])';
  const parts: string[] = [`/* ── CarFlow4u vzhled: ${t.label} ── */`];

  parts.push(`
    .app-sidebar { background-color: ${t.sidebar} !important; color: ${lightSidebar ? '#111827' : '#ffffff'} !important; ${lightSidebar ? `border-right: 1px solid ${t.sidebarBorder};` : ''} }
    .app-sidebar-border { border-color: ${t.sidebarBorder} !important; }
    .app-sidebar-text { color: ${t.sidebarText} !important; }
    .app-sidebar-hover:hover { background-color: ${t.sidebarHover} !important; ${lightSidebar ? 'color:#111827 !important;' : ''} }
    .app-sidebar-active { background-color: ${t.sidebarActive} !important; color: #fff !important; }
    .app-sidebar-logo { background-color: ${t.accent} !important; }

    .bg-blue-600 { background-color: ${t.accent} !important; }
    .bg-blue-700 { background-color: ${t.accentHover} !important; }
    .hover\\:bg-blue-700:hover { background-color: ${t.accentHover} !important; }
    .disabled\\:opacity-40:disabled { opacity: 0.4 !important; }

    .text-blue-600 { color: ${t.accent} !important; }
    .text-blue-700 { color: ${t.accentText} !important; }
    .text-blue-800 { color: ${t.accentText} !important; }
    .hover\\:text-blue-700:hover { color: ${t.accentHover} !important; }
    .border-blue-600 { border-color: ${t.accent} !important; }
    .border-blue-500 { border-color: ${t.accent} !important; }

    .bg-blue-50  { background-color: ${t.accentLight} !important; }
    .bg-blue-100 { background-color: ${t.accentLight} !important; }

    .focus\\:ring-blue-500:focus { --tw-ring-color: ${t.accent} !important; }
    .focus\\:ring-2:focus { --tw-ring-color: ${t.accent} !important; }

    .settings-nav-active { background-color: ${t.accent} !important; color: #fff !important; }
  `);

  // písmo
  const px = SCALE_PX[cfg.fontScale ?? 'md'];
  if (px !== 16) parts.push(`html { font-size: ${px}px !important; }`);
  const stack = FONT_STACKS[cfg.font ?? 'default'];
  if (stack) parts.push(`body, button, input, select, textarea { font-family: ${stack} !important; }`);

  // pozadí a horní lišta
  const bg = cfg.bg ?? 'default';
  if (bg !== 'default') {
    const val = bg === 'tint' ? tint(t.accent, 0.94)
      : bg === 'gradient' ? `linear-gradient(160deg, ${tint(t.accent, 0.93)} 0%, #f8fafc 55%, ${tint(t.accent, 0.97)} 100%)`
      : BG_COLORS[bg];
    parts.push(`.app-bg { background: ${val} !important; }`);
  }
  if (cfg.topbar === 'tint') parts.push(`.app-topbar { background-color: ${tint(t.accent, 0.92)} !important; border-color: ${tint(t.accent, 0.8)} !important; }`);
  if (cfg.topbar === 'clear') parts.push(`.app-topbar { background-color: transparent !important; border-color: transparent !important; }`);

  // zaoblení
  if (cfg.radius && cfg.radius !== 'normal') {
    const [md, lg, xl, x2] = RADII[cfg.radius];
    parts.push(`.rounded, .rounded-md { border-radius: ${md}px !important; }
      .rounded-lg { border-radius: ${lg}px !important; }
      .rounded-xl { border-radius: ${xl}px !important; }
      .rounded-2xl, .rounded-3xl { border-radius: ${x2}px !important; }`);
  }

  // tlačítka
  if (cfg.buttonShape === 'pill') parts.push(`${SOLID_BTN}, button.bg-blue-600, a.bg-blue-600 { border-radius: 9999px !important; }`);
  if (cfg.buttonShape === 'square') parts.push(`${SOLID_BTN}, button.bg-blue-600, a.bg-blue-600 { border-radius: 3px !important; }`);
  if (cfg.buttonStyle === 'gradient') {
    parts.push(`button.bg-blue-600:not(.app-sidebar-active), a.bg-blue-600:not(.app-sidebar-active) { background-image: linear-gradient(135deg, ${tint(t.accent, 0.14)}, ${shade(t.accent, 0.12)}) !important; box-shadow: 0 1px 2px rgba(16,24,40,.18), inset 0 1px 0 rgba(255,255,255,.18); }
      button.bg-blue-600:not(.app-sidebar-active):hover, a.bg-blue-600:not(.app-sidebar-active):hover { filter: brightness(.95); }`);
  }
  if (cfg.buttonStyle === 'soft') {
    parts.push(`button.bg-blue-600:not(.app-sidebar-active), a.bg-blue-600:not(.app-sidebar-active) { background-color: ${t.accentLight} !important; color: ${t.accentText} !important; }
      button.bg-blue-600:not(.app-sidebar-active):hover, a.bg-blue-600:not(.app-sidebar-active):hover { background-color: ${tint(t.accent, 0.78)} !important; }`);
  }

  // karty
  if (cfg.cards === 'shadow') {
    parts.push(`div[class*="bg-white"][class*="border"][class*="rounded-"] { box-shadow: 0 1px 3px rgba(16,24,40,.05), 0 8px 24px -8px rgba(16,24,40,.12) !important; border-color: rgba(16,24,40,.05) !important; }`);
  }
  if (cfg.cards === 'flat') {
    parts.push(`div[class*="bg-white"][class*="border"][class*="rounded-"] { box-shadow: none !important; }`);
  }

  // hustota
  if (cfg.density === 'compact') {
    parts.push(`.app-main { padding: 1rem !important; }
      table :is(td, th) { padding-top: .35rem !important; padding-bottom: .35rem !important; }
      .app-nav-item { padding-top: .4rem !important; padding-bottom: .4rem !important; }`);
  }
  if (cfg.density === 'roomy') {
    parts.push(`.app-main { padding: 2rem !important; }
      table :is(td, th) { padding-top: .95rem !important; padding-bottom: .95rem !important; }
      .app-nav-item { padding-top: .75rem !important; padding-bottom: .75rem !important; }`);
  }

  return parts.join('\n');
}

const LS_KEY = 'app_theme_id';
const STYLE_ID = 'app-theme-override';

function isPlain(themeId: string, cfg: UiConfig): boolean {
  return themeId === 'default' && Object.keys(cfg).length === 0;
}

// ── Režim světlý / tmavý / podle systému (osobní nastavení, jen v tomto prohlížeči) ──
export type UiMode = 'light' | 'dark' | 'auto';
const MODE_KEY = 'ui_mode_v1';

export function getUiMode(): UiMode {
  try { const v = localStorage.getItem(MODE_KEY); return v === 'dark' || v === 'auto' ? v : 'light'; } catch { return 'light'; }
}
export function isDarkActive(mode: UiMode = getUiMode()): boolean {
  if (mode === 'dark') return true;
  if (mode === 'auto') { try { return window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; } }
  return false;
}

let lastPreview: { themeId: string; cfg: UiConfig } = { themeId: 'default', cfg: {} };
let mqBound = false;

/** Použije vzhled na stránku (bez ukládání) – pro živý náhled v Nastavení. */
export function previewTheme(themeId: string, cfg: UiConfig) {
  lastPreview = { themeId, cfg };
  const dark = isDarkActive();
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  const plain = isPlain(themeId, cfg);
  const light = plain ? '' : buildThemeCSS(resolveTheme(themeId, cfg), cfg);
  el.textContent = dark ? light + '\n' + buildDarkCSS(resolveTheme(themeId, cfg, true), cfg) : light;
  document.documentElement.classList.toggle('dark', dark);
  if (!mqBound) {
    mqBound = true;
    try {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (getUiMode() === 'auto') { previewTheme(lastPreview.themeId, lastPreview.cfg); window.dispatchEvent(new Event('ui-mode-change')); }
      });
    } catch { /* starší prohlížeč */ }
  }
}

/** Přepne režim (jen pro tento prohlížeč) a hned ho promítne. */
export function setUiMode(mode: UiMode) {
  try { localStorage.setItem(MODE_KEY, mode); } catch { /* ignore */ }
  previewTheme(lastPreview.themeId, lastPreview.cfg);
  window.dispatchEvent(new Event('ui-mode-change'));
}

export function applyTheme(themeId: string) {
  previewTheme(themeId, getUiConfig());
}

export function loadAndApplyTheme() {
  const saved = getShared(LS_KEY) ?? 'default';
  applyTheme(saved);
  return saved;
}

export function saveTheme(themeId: string) {
  setSharedNoWait(LS_KEY, themeId);
  applyTheme(themeId);
}

/** Uloží téma i ostatní volby vzhledu na server (platí pro celou firmu). */
export async function saveAppearance(themeId: string, cfg: UiConfig): Promise<void> {
  await setShared(LS_KEY, themeId);
  await setShared(UI_KEY, JSON.stringify(sanitizeUi(cfg)));
  previewTheme(themeId, sanitizeUi(cfg));
}

export function getSavedThemeId(): string {
  return getShared(LS_KEY) ?? 'default';
}
